// SIOverlay.tsx
import React, { FC, cloneElement, isValidElement, useId } from "react";

import { Popover, PopoverPlacement } from "Cobalt/Popover";
import OverlayTrackingWrapper from "Common/OverlayTrackingWrapper";

import makeBem from "Utils/makeBem";

interface SIOverlayProps {
  overlayTarget: React.ReactNode;
  overlayContent: React.ReactNode;
  trackType?: string;
  trackId?: string;
  placement?: "right" | "left" | "top" | "bottom";
  /** (optional) showInitially - if true, the popover will be shown initially */
  showInitially?: boolean;
}

const bem = makeBem("SIOverlay");

const mapPlacement = (placement: SIOverlayProps["placement"]): PopoverPlacement => {
  if (placement === "top") return "up";
  if (placement === "bottom") return "down";
  return placement || "right";
};

const isPopoverElement = (node: React.ReactNode): node is React.ReactElement<any> =>
  isValidElement(node) && node.type && (node.type as any).displayName === "Popover";

const SIOverlay: FC<SIOverlayProps> = ({ overlayTarget, overlayContent, trackType, trackId, placement = "right", showInitially }) => {
  const targetId = `si-overlay-${useId().replace(/:/g, "")}`;
  const popoverPlacement = mapPlacement(placement);
  const popoverContent = isPopoverElement(overlayContent) ? (
    cloneElement(overlayContent, {
      id: targetId,
      placement: popoverPlacement,
      showBackdrop: false,
    })
  ) : (
    <Popover id={targetId} placement={popoverPlacement} showBackdrop={false} body>
      {overlayContent}
    </Popover>
  );

  return (
    <OverlayTrackingWrapper trackType={trackType} trackId={trackId}>
      <Popover.Trigger
        as="span"
        target={targetId}
        trigger="hover"
        placement={popoverPlacement}
        className={bem("wrapper")}
        data-show-initially={showInitially || undefined}
      >
        {overlayTarget}
      </Popover.Trigger>
      {popoverContent}
    </OverlayTrackingWrapper>
  );
};

export default SIOverlay;
